import { useEffect, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { setTokens } from "@/lib/auth";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import type { admin_role } from "@/hooks/useAdminAuth";

const OTP_LENGTH = 6;
const COOLDOWN_SECONDS = 60;

export default function LoginPage() {
  const [login_mode, set_login_mode] = useState<"otp" | "password">("password");
  const [email, set_email] = useState("");
  const [password, set_password] = useState("");
  const [show_password, set_show_password] = useState(false);
  const [otp, set_otp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [demo_code, set_demo_code] = useState("");
  const [cooldown, set_cooldown] = useState(0);
  const [is_sending, set_is_sending] = useState(false);
  const [is_verifying, set_is_verifying] = useState(false);
  const [is_password_loading, set_is_password_loading] = useState(false);
  const [error, set_error] = useState("");

  const otp_refs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const { login } = useAdminAuth();

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => set_cooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const is_email_valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const otp_value = otp.join("");
  const is_otp_complete = otp_value.length === OTP_LENGTH;

  function switch_mode(mode: "otp" | "password") {
    set_login_mode(mode);
    set_error("");
    set_password("");
    set_show_password(false);
  }

  const handle_send_code = async () => {
    set_error("");
    if (!is_email_valid) {
      set_error("请输入有效的邮箱地址");
      return;
    }
    set_is_sending(true);
    try {
      const res = await api.post("/quantum/business/auth/send-code", { email });
      const { demo_code: dc } = res.data.data;
      set_demo_code(dc || "");
      set_cooldown(COOLDOWN_SECONDS);
      set_otp(Array(OTP_LENGTH).fill(""));
      otp_refs.current[0]?.focus();
    } catch {
      set_error("发送验证码失败，请稍后再试");
    } finally {
      set_is_sending(false);
    }
  };

  const handle_otp_change = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
      const next = [...Array(OTP_LENGTH).fill("")];
      digits.split("").forEach((d, i) => { next[i] = d; });
      set_otp(next);
      otp_refs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus();
      return;
    }
    const digit = value.replace(/\D/g, "");
    const next = [...otp];
    next[index] = digit;
    set_otp(next);
    if (digit && index < OTP_LENGTH - 1) {
      otp_refs.current[index + 1]?.focus();
    }
  };

  const handle_otp_keydown = (index: number, e: ReactKeyboardEvent) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const next = [...otp];
        next[index] = "";
        set_otp(next);
      } else if (index > 0) {
        otp_refs.current[index - 1]?.focus();
      }
    }
  };

  const handle_verify = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (login_mode !== "otp") return;
    set_error("");
    if (!is_otp_complete) {
      set_error("请输入完整的验证码");
      return;
    }
    set_is_verifying(true);
    try {
      const res = await api.post("/quantum/business/auth/verify-code", {
        email,
        code: otp_value,
      });
      const {
        token,
        access_token,
        refresh_token,
        expires_in = 15 * 60,
        user,
        env_options = [],
        current_env = null,
      } = res.data.data;

      setTokens({
        access_token: access_token || token,
        refresh_token: refresh_token || "",
        expires_in,
        user_role: user.role,
      });
      localStorage.setItem(
        "quantum_admin",
        JSON.stringify({
          id: user.user_id,
          email: user.email,
          role: user.role,
          totp_verified: true,
          is_active: user.is_active,
        })
      );
      login({
        id: user.user_id,
        email: user.email,
        role: user.role as admin_role,
        totp_verified: true,
        is_active: user.is_active,
      }, env_options, current_env);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || "验证码错误，请重试";
      set_error(msg);
    } finally {
      set_is_verifying(false);
    }
  };

  const handle_password_login = async () => {
    set_error("");
    if (!is_email_valid) {
      set_error("请输入有效的邮箱地址");
      return;
    }
    if (!password) {
      set_error("请输入密码");
      return;
    }
    set_is_password_loading(true);
    try {
      const res = await api.post("/quantum/business/auth/login", { email, password });
      const {
        token,
        access_token,
        refresh_token,
        expires_in = 15 * 60,
        user,
        env_options = [],
        current_env = null,
      } = res.data.data;

      setTokens({
        access_token: access_token || token,
        refresh_token: refresh_token || "",
        expires_in,
        user_role: user.role,
      });
      localStorage.setItem(
        "quantum_admin",
        JSON.stringify({
          id: user.user_id,
          email: user.email,
          role: user.role,
          totp_verified: true,
          is_active: user.is_active,
        })
      );
      login({
        id: user.user_id,
        email: user.email,
        role: user.role as admin_role,
        totp_verified: true,
        is_active: user.is_active,
      }, env_options, current_env);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        set_error(status === 401 ? "邮箱或密码错误" : "登录失败，请稍后重试");
      } else {
        set_error("登录失败，请稍后重试");
      }
    } finally {
      set_is_password_loading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <div className="w-full max-w-[520px]">
        {/* Logo */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-brand mx-auto mb-4 sm:mb-5 flex items-center justify-center shadow-lg shadow-brand/20">
            <i className="ri-bar-chart-grouped-line text-white text-2xl sm:text-3xl" />
          </div>
          <h1 className="text-text-primary text-2xl sm:text-3xl font-semibold mb-1.5 sm:mb-2">钜睿科技</h1>
          <p className="text-text-muted text-sm sm:text-base">量化交易管理后台</p>
        </div>

        {/* Card */}
        <form
          onSubmit={handle_verify}
          className="bg-surface-card rounded-2xl p-6 sm:p-10 border border-surface-border shadow-xl shadow-gray-200/40"
        >
          {/* Tab Switcher */}
          <div className="flex mb-6 bg-surface rounded-xl p-1 border border-surface-border">
            <button
              type="button"
              onClick={() => switch_mode("password")}
              className={`flex-1 h-10 rounded-lg text-sm font-medium transition-colors ${
                login_mode === "password"
                  ? "bg-brand text-white shadow-sm"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              密码登录
            </button>
            <button
              type="button"
              onClick={() => switch_mode("otp")}
              className={`flex-1 h-10 rounded-lg text-sm font-medium transition-colors ${
                login_mode === "otp"
                  ? "bg-brand text-white shadow-sm"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              验证码登录
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3" role="alert">
              <i className="ri-error-warning-line text-status-danger text-base mt-0.5" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Email Field — shared between tabs */}
          <div className="mb-5">
            <label className="block text-text-secondary text-sm mb-2.5 font-medium" htmlFor="login-email">邮箱地址</label>
            <div className="relative">
              <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-text-muted text-lg" />
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => set_email(e.target.value)}
                placeholder="请输入管理员邮箱"
                disabled={is_sending || is_verifying || is_password_loading}
                autoComplete="email"
                className="w-full h-14 pl-12 pr-4 rounded-xl bg-surface-card border border-surface-border text-text-primary text-base placeholder:text-text-muted focus:border-brand/50 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* OTP Section */}
          {login_mode === "otp" && (
            <>
              <div className="mb-6">
                <label className="block text-text-secondary text-sm mb-2.5 font-medium">验证码</label>
                <div className="flex gap-2 sm:gap-3 justify-between">
                  {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                    <input
                      key={i}
                      ref={(el) => { otp_refs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={OTP_LENGTH}
                      value={otp[i]}
                      onChange={(e) => handle_otp_change(i, e.target.value)}
                      onKeyDown={(e) => handle_otp_keydown(i, e)}
                      disabled={is_verifying}
                      aria-label={`验证码第 ${i + 1} 位`}
                      className="flex-1 h-14 rounded-xl bg-surface-card border border-surface-border text-text-primary text-xl font-semibold text-center focus:border-brand/50 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                    />
                  ))}
                </div>
                {demo_code && demo_code !== "------" && (
                  <p className="text-text-muted text-xs mt-2">演示环境默认认证码: {demo_code}</p>
                )}
              </div>

              {!is_otp_complete && (
                <button
                  type="button"
                  onClick={handle_send_code}
                  disabled={!is_email_valid || is_sending || cooldown > 0}
                  className="w-full h-14 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-lg shadow-brand/20"
                >
                  {is_sending ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-lg" />
                      发送中...
                    </span>
                  ) : cooldown > 0 ? (
                    <span>{cooldown}s 后重新发送</span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line text-lg" />
                      发送验证码
                    </span>
                  )}
                </button>
              )}

              {is_otp_complete && (
                <button
                  type="submit"
                  disabled={is_verifying}
                  className="w-full h-14 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-lg shadow-brand/20 mt-3"
                >
                  {is_verifying ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-lg" />
                      验证中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-login-circle-line text-lg" />
                      登录
                    </span>
                  )}
                </button>
              )}
            </>
          )}

          {/* Password Section */}
          {login_mode === "password" && (
            <>
              <div className="mb-6">
                <label className="block text-text-secondary text-sm mb-2.5 font-medium" htmlFor="login-password">密码</label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-text-muted text-lg" />
                  <input
                    id="login-password"
                    type={show_password ? "text" : "password"}
                    value={password}
                    onChange={(e) => set_password(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handle_password_login(); }}
                    placeholder="请输入密码"
                    disabled={is_password_loading}
                    autoComplete="current-password"
                    className="w-full h-14 pl-12 pr-12 rounded-xl bg-surface-card border border-surface-border text-text-primary text-base placeholder:text-text-muted focus:border-brand/50 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={() => set_show_password((v) => !v)}
                    tabIndex={-1}
                    aria-label={show_password ? "隐藏密码" : "显示密码"}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors"
                  >
                    <i className={`${show_password ? "ri-eye-off-line" : "ri-eye-line"} text-lg`} />
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handle_password_login}
                disabled={is_password_loading || !is_email_valid || !password}
                className="w-full h-14 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-lg shadow-brand/20"
              >
                {is_password_loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-lg" />
                    登录中...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-login-circle-line text-lg" />
                    登录
                  </span>
                )}
              </button>
            </>
          )}
        </form>

        <p className="text-center text-text-muted/60 text-xs sm:text-sm mt-6 sm:mt-8">
          无注册入口，管理员账号由超级管理员创建
        </p>
      </div>
    </div>
  );
}
